/* ============================================
   Notificaciones — Templates & Logs Manager
   Full CRUD for notification templates +
   log viewer + test send functionality
   ============================================ */
import { useState, useRef } from 'react'
import { useSupabase } from '@/hooks/useSupabase'
import { useCachedData } from '@/hooks/useCachedData'
import { showToast } from '@/components/layout/Toast'
import StatCard from '@/components/shared/StatCard'
import EmptyState from '@/components/shared/EmptyState'
import {
  IconBell, IconMail, IconMessageCircle, IconPlus, IconEdit,
  IconTrash, IconSend, IconRefresh, IconCheck, IconCheckCircle,
  IconXCircle, IconChevronDown, IconZap, IconEye,
} from '@/components/icons/Icons'
import type { NotificacionTemplate, NotificacionLog, CanalNotificacion } from '@/lib/types'
import { PageHeader } from '@/components/ui'

/* ─── Constants ─── */

const EVENTOS = [
  'cotizacion_enviada', 'cotizacion_aceptada',
  'factura_emitida', 'factura_pagada',
  'tarea_completada', 'tarea_aprobada',
  'proyecto_iniciado', 'proyecto_completado',
  'recordatorio_pago', 'bienvenida_portal',
  'password_reset', 'reunion_agendada',
  'soporte_excedido', 'contrato_firmado',
] as const

const ROLES = ['principal', 'contabilidad', 'admin'] as const

const VARIABLES = [
  { key: '{{nombre}}', label: 'Nombre' },
  { key: '{{empresa}}', label: 'Empresa' },
  { key: '{{numero}}', label: 'Número' },
  { key: '{{monto}}', label: 'Monto' },
  { key: '{{fecha}}', label: 'Fecha' },
  { key: '{{url}}', label: 'URL' },
]

const EXAMPLE_VALUES: Record<string, string> = {
  '{{nombre}}': 'Juan Pérez',
  '{{empresa}}': 'Acme Corp S.A.C.',
  '{{numero}}': 'COT-2026-042',
  '{{monto}}': 'S/ 5,400.00',
  '{{fecha}}': '25/05/2026',
  '{{url}}': 'https://1conecta-erp.pages.dev/ver/abc123',
}

const emptyTemplate = (): Omit<NotificacionTemplate, 'id' | 'created_at'> => ({
  evento: EVENTOS[0],
  canal: 'email',
  destinatario_rol: 'principal',
  asunto: '',
  template_texto: '',
  template_html: null,
  variables: [],
  activo: true,
})

/** Extract {{variable}} patterns from text */
function extractVars(text: string): string[] {
  const matches = text.match(/\{\{[a-z_]+\}\}/g)
  return matches ? [...new Set(matches)] : []
}

/** Replace variables with example values for preview */
function previewText(text: string): string {
  let result = text
  for (const [key, val] of Object.entries(EXAMPLE_VALUES)) {
    result = result.split(key).join(val)
  }
  return result
}

/** Human-readable event name */
function formatEvento(ev: string): string {
  return ev.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

export default function Notificaciones() {
  const db = useSupabase()
  const [logsOpen, setLogsOpen] = useState(false)

  // Modal state
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<NotificacionTemplate | null>(null)
  const [form, setForm] = useState(emptyTemplate())
  const [saving, setSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(false)

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<NotificacionTemplate | null>(null)

  // Test send state
  const [testTarget, setTestTarget] = useState<NotificacionTemplate | null>(null)
  const [testAddress, setTestAddress] = useState('')
  const [testSending, setTestSending] = useState(false)

  // Textarea ref for cursor insertion
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const { data: templates = [], isLoading: loading, refrescar: loadAll } = useCachedData(
    ['notificacion_templates'],
    () => db.getAll<NotificacionTemplate>('notificacion_templates')
      .then(d => d.sort((a, b) => a.evento.localeCompare(b.evento)))
      .catch(() => {
        showToast('Error al cargar templates', 'error')
        return [] as NotificacionTemplate[]
      }),
  )

  // El log es lo que se mira para ver si un envio salio, asi que un minuto:
  // cinco dejarian un envio reciente fuera de la lista.
  const { data: logs = [], isFetching: logsLoading, refrescar: loadLogs } = useCachedData(
    ['notificacion_log'],
    () => db.getAll<NotificacionLog>('notificacion_log')
      .then(d => d.sort((a, b) => new Date(b.enviado_at).getTime() - new Date(a.enviado_at).getTime()).slice(0, 50))
      .catch(() => {
        showToast('Error al cargar logs', 'error')
        return [] as NotificacionLog[]
      }),
    { staleTime: 60 * 1000 },
  )

  /* ─── KPI Calculations ─── */
  const activeTemplates = templates.filter(t => t.activo).length
  const todayStr = new Date().toISOString().slice(0, 10)
  const todayLogs = logs.filter(l => l.enviado_at.slice(0, 10) === todayStr)
  const sentToday = todayLogs.length
  const successRate = todayLogs.length > 0
    ? Math.round((todayLogs.filter(l => l.estado === 'enviado').length / todayLogs.length) * 100)
    : 100
  const canalesActivos = new Set(templates.filter(t => t.activo).map(t => t.canal)).size

  /* ─── Modal Handlers ─── */
  const openCreate = () => {
    setEditing(null)
    setForm(emptyTemplate())
    setShowPreview(false)
    setShowModal(true)
  }

  const openEdit = (t: NotificacionTemplate) => {
    setEditing(t)
    setForm({
      evento: t.evento,
      canal: t.canal,
      destinatario_rol: t.destinatario_rol,
      asunto: t.asunto,
      template_texto: t.template_texto,
      template_html: t.template_html,
      variables: t.variables,
      activo: t.activo,
    })
    setShowPreview(false)
    setShowModal(true)
  }

  const closeModal = () => { setShowModal(false); setEditing(null) }

  const saveForm = async () => {
    if (!form.template_texto.trim()) {
      showToast('El texto del template es obligatorio', 'error')
      return
    }
    if (form.canal === 'email' && !form.asunto?.trim()) {
      showToast('El asunto es obligatorio para email', 'error')
      return
    }

    const variables = extractVars(form.template_texto)
    const payload = { ...form, variables }

    setSaving(true)
    try {
      if (editing) {
        await db.update('notificacion_templates', { id: editing.id, ...payload })
        showToast('Template actualizado', 'success')
      } else {
        await db.add('notificacion_templates', payload as Record<string, unknown>)
        showToast('Template creado', 'success')
      }
      closeModal()
      await loadAll()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al guardar', 'error')
    } finally { setSaving(false) }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await db.remove('notificacion_templates', deleteTarget.id)
      showToast('Template eliminado', 'success')
      setDeleteTarget(null)
      await loadAll()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al eliminar', 'error')
    }
  }

  const toggleActivo = async (t: NotificacionTemplate) => {
    try {
      await db.update('notificacion_templates', { id: t.id, activo: !t.activo })
      await loadAll()
      showToast(`Template ${!t.activo ? 'activado' : 'desactivado'}`, 'info')
    } catch { showToast('Error al actualizar', 'error') }
  }

  const insertVariable = (variable: string) => {
    const ta = textareaRef.current
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const text = form.template_texto
    const newText = text.substring(0, start) + variable + text.substring(end)
    setForm(f => ({ ...f, template_texto: newText }))
    // Restore focus and cursor position after state update
    requestAnimationFrame(() => {
      ta.focus()
      const pos = start + variable.length
      ta.setSelectionRange(pos, pos)
    })
  }

  /* ─── Test Send ─── */
  const openTestSend = (t: NotificacionTemplate) => {
    setTestTarget(t)
    setTestAddress('')
    setTestSending(false)
  }

  const sendTest = async () => {
    if (!testTarget || !testAddress.trim()) {
      showToast('Ingresa una dirección', 'error')
      return
    }
    setTestSending(true)
    try {
      // Log the test send attempt
      await db.add('notificacion_log', {
        template_id: testTarget.id,
        evento: testTarget.evento,
        canal: testTarget.canal,
        estado: 'enviado',
        metadata: {
          test: true,
          address: testAddress,
          template_preview: previewText(testTarget.template_texto).slice(0, 200),
        },
        enviado_at: new Date().toISOString(),
      } as Record<string, unknown>)
      showToast(`Prueba registrada para ${testAddress}`, 'success')
      setTestTarget(null)
      if (logsOpen) await loadLogs()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al enviar prueba', 'error')
    } finally { setTestSending(false) }
  }

  /* ─── Accordion Toggle ─── */
  const toggleLogs = () => {
    if (!logsOpen) {
      setLogsOpen(true)
      loadLogs()
    } else {
      setLogsOpen(false)
    }
  }

  /* ─── Detected variables from current form ─── */
  const detectedVars = extractVars(form.template_texto)

  /* ─── Render ─── */

  if (loading) return (
    <div className="page-enter" style={{ padding: '2rem' }}>
      <PageHeader breadcrumbs={[{ label: 'Notificaciones' }]} />
      <div style={{ color: 'var(--text-secondary)', textAlign: 'center', marginTop: 40 }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...
      </div>
    </div>
  )

  return (
    <div className="page-enter notif-page">
      <PageHeader
        breadcrumbs={[{ label: 'Notificaciones' }]}
        actions={
          <button className="btn btn-primary" onClick={openCreate}>
            <IconPlus size={16} /> Nuevo Template
          </button>
        }
      />

      {/* ─── KPI Stats ─── */}
      <div className="notif-stats">
        <StatCard
          icon={<IconBell size={20} />}
          label="Templates Activos"
          value={activeTemplates}
          color="primary"
        />
        <StatCard
          icon={<IconSend size={20} />}
          label="Enviados Hoy"
          value={sentToday}
          color="info"
        />
        <StatCard
          icon={<IconCheckCircle size={20} />}
          label="Tasa de Éxito"
          value={`${successRate}%`}
          color={successRate >= 90 ? 'success' : successRate >= 70 ? 'warning' : 'danger'}
        />
        <StatCard
          icon={<IconZap size={20} />}
          label="Canales Activos"
          value={canalesActivos}
          color="success"
        />
      </div>

      {/* ─── Template List ─── */}
      {templates.length === 0 ? (
        <EmptyState
          icon={<IconBell size={48} />}
          title="Sin templates de notificación"
          description="Crea tu primer template para automatizar notificaciones por WhatsApp o Email"
          actionLabel="+ Nuevo Template"
          onAction={openCreate}
        />
      ) : (
        <div className="notif-grid">
          {templates.map(t => (
            <div className={`notif-card ${!t.activo ? 'notif-card--inactive' : ''}`} key={t.id}>
              {/* Card header */}
              <div className="notif-card__header">
                <div className="notif-card__badges">
                  <span className="notif-card__evento-badge">{formatEvento(t.evento)}</span>
                  <span className={`notif-card__canal-badge notif-card__canal-badge--${t.canal}`}>
                    {t.canal === 'whatsapp'
                      ? <><IconMessageCircle size={13} /> WhatsApp</>
                      : <><IconMail size={13} /> Email</>
                    }
                  </span>
                </div>
                <div className="notif-card__actions">
                  <button
                    className={`notif-card__toggle ${t.activo ? 'notif-card__toggle--on' : ''}`}
                    onClick={() => toggleActivo(t)}
                    title={t.activo ? 'Desactivar' : 'Activar'}
                    type="button"
                  >
                    <span className="notif-card__toggle-dot" />
                  </button>
                  <button className="btn btn-sm notif-card__btn" onClick={() => openTestSend(t)} type="button" title="Enviar prueba">
                    <IconSend size={13} />
                  </button>
                  <button className="btn btn-sm notif-card__btn" onClick={() => openEdit(t)} type="button" title="Editar">
                    <IconEdit size={13} />
                  </button>
                  <button className="btn btn-sm notif-card__btn notif-card__btn--danger" onClick={() => setDeleteTarget(t)} type="button" title="Eliminar">
                    <IconTrash size={13} />
                  </button>
                </div>
              </div>

              {/* Meta */}
              <div className="notif-card__meta">
                <span className="notif-card__rol">
                  Destinatario: <strong>{t.destinatario_rol}</strong>
                </span>
                {t.canal === 'email' && t.asunto && (
                  <span className="notif-card__asunto">
                    Asunto: {t.asunto}
                  </span>
                )}
              </div>

              {/* Preview */}
              <div className="notif-card__preview">
                {t.template_texto.length > 120
                  ? t.template_texto.slice(0, 120) + '…'
                  : t.template_texto
                }
              </div>

              {/* Variables chips */}
              {t.variables.length > 0 && (
                <div className="notif-card__vars">
                  {t.variables.map(v => (
                    <span className="notif-var-chip" key={v}>{v}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ─── Log Viewer Accordion ─── */}
      <div className="notif-log-section">
        <button className="notif-log-toggle" onClick={toggleLogs} type="button">
          <div className="notif-log-toggle__left">
            <IconBell size={18} />
            <span>Historial de Envíos</span>
            <span className="notif-log-toggle__count">{logsOpen ? logs.length : ''}</span>
          </div>
          <IconChevronDown size={16} className={`notif-log-chevron ${logsOpen ? 'notif-log-chevron--open' : ''}`} />
        </button>

        {logsOpen && (
          <div className="notif-log-content">
            <div className="notif-log-toolbar">
              <button className="btn btn-sm btn-secondary" onClick={() => void loadLogs()} disabled={logsLoading} type="button">
                <IconRefresh size={14} /> {logsLoading ? 'Cargando...' : 'Actualizar'}
              </button>
            </div>

            {logs.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', padding: 16, textAlign: 'center' }}>
                Sin registros de envío
              </p>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Evento</th>
                      <th>Canal</th>
                      <th>Estado</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(l => (
                      <tr key={l.id}>
                        <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                          {new Date(l.enviado_at).toLocaleString('es-PE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td>
                          <span className="notif-card__evento-badge" style={{ fontSize: '0.7rem', padding: '2px 8px' }}>
                            {formatEvento(l.evento)}
                          </span>
                        </td>
                        <td>
                          <span className={`notif-card__canal-badge notif-card__canal-badge--${l.canal}`} style={{ fontSize: '0.7rem', padding: '2px 8px' }}>
                            {l.canal === 'whatsapp' ? 'WA' : 'Email'}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${l.estado === 'enviado' ? 'notif-badge--enviado' : 'notif-badge--fallido'}`}>
                            {l.estado === 'enviado'
                              ? <><IconCheck size={11} /> Enviado</>
                              : <><IconXCircle size={11} /> Fallido</>
                            }
                          </span>
                        </td>
                        <td style={{ fontSize: '0.8rem', color: l.error ? 'var(--danger)' : 'var(--text-muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {l.error || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Create/Edit Modal ─── */}
      {showModal && (
        <div className="modal-backdrop" onClick={closeModal}>
          <div className="modal notif-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Editar Template' : 'Nuevo Template de Notificación'}</h2>
              <button className="modal-close" onClick={closeModal} type="button">×</button>
            </div>
            <div className="modal-body">
              {/* Row 1: Evento + Canal */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Evento *</label>
                  <select
                    className="form-select"
                    value={form.evento}
                    onChange={e => setForm(f => ({ ...f, evento: e.target.value }))}
                  >
                    {EVENTOS.map(ev => (
                      <option key={ev} value={ev}>{formatEvento(ev)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Canal *</label>
                  <select
                    className="form-select"
                    value={form.canal}
                    onChange={e => setForm(f => ({ ...f, canal: e.target.value as CanalNotificacion }))}
                  >
                    <option value="email">📧 Email</option>
                    <option value="whatsapp">💬 WhatsApp</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Destinatario + Activo */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Destinatario Rol *</label>
                  <select
                    className="form-select"
                    value={form.destinatario_rol}
                    onChange={e => setForm(f => ({ ...f, destinatario_rol: e.target.value }))}
                  >
                    {ROLES.map(r => (
                      <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 24 }}>
                    <input
                      type="checkbox"
                      checked={form.activo}
                      onChange={e => setForm(f => ({ ...f, activo: e.target.checked }))}
                    />
                    Template Activo
                  </label>
                </div>
              </div>

              {/* Asunto (email only) */}
              {form.canal === 'email' && (
                <div className="form-group">
                  <label className="form-label">Asunto *</label>
                  <input
                    className="form-input"
                    value={form.asunto ?? ''}
                    onChange={e => setForm(f => ({ ...f, asunto: e.target.value }))}
                    placeholder="Ej: Cotización {{numero}} enviada"
                  />
                </div>
              )}

              {/* Variable toolbar */}
              <div className="form-group">
                <label className="form-label">Texto del Template *</label>
                <div className="notif-var-toolbar">
                  <span className="notif-var-toolbar__label">Insertar variable:</span>
                  {VARIABLES.map(v => (
                    <button
                      key={v.key}
                      className="notif-var-btn"
                      type="button"
                      onClick={() => insertVariable(v.key)}
                      title={`Insertar ${v.key}`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
                <textarea
                  ref={textareaRef}
                  className="form-textarea notif-template-textarea"
                  value={form.template_texto}
                  onChange={e => setForm(f => ({ ...f, template_texto: e.target.value }))}
                  placeholder="Hola {{nombre}}, tu cotización {{numero}} por {{monto}} ha sido enviada..."
                  rows={6}
                />
              </div>

              {/* Detected variables */}
              {detectedVars.length > 0 && (
                <div className="notif-detected-vars">
                  <span className="notif-detected-vars__label">Variables detectadas:</span>
                  {detectedVars.map(v => (
                    <span className="notif-var-chip" key={v}>{v}</span>
                  ))}
                </div>
              )}

              {/* Live preview toggle */}
              <div className="notif-preview-section">
                <button
                  className="btn btn-sm btn-secondary"
                  type="button"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  <IconEye size={14} /> {showPreview ? 'Ocultar Preview' : 'Ver Preview'}
                </button>

                {showPreview && form.template_texto.trim() && (
                  <div className="notif-preview-box">
                    <div className="notif-preview-box__header">
                      <span>Vista Previa</span>
                      {form.canal === 'email' && form.asunto && (
                        <span className="notif-preview-box__asunto">
                          Asunto: {previewText(form.asunto)}
                        </span>
                      )}
                    </div>
                    <div className={`notif-preview-box__body notif-preview-box__body--${form.canal}`}>
                      {previewText(form.template_texto)}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal} type="button">Cancelar</button>
              <button className="btn btn-primary" onClick={saveForm} disabled={saving} type="button">
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Delete Confirmation Modal ─── */}
      {deleteTarget && (
        <div className="modal-backdrop" onClick={() => setDeleteTarget(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h2>Eliminar Template</h2>
              <button className="modal-close" onClick={() => setDeleteTarget(null)} type="button">×</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                ¿Estás seguro de que deseas eliminar el template{' '}
                <strong style={{ color: 'var(--text-primary)' }}>{formatEvento(deleteTarget.evento)}</strong>{' '}
                ({deleteTarget.canal})? Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)} type="button">Cancelar</button>
              <button className="btn btn-danger" onClick={confirmDelete} type="button">Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Test Send Mini-Modal ─── */}
      {testTarget && (
        <div className="modal-backdrop" onClick={() => setTestTarget(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h2>Enviar Prueba</h2>
              <button className="modal-close" onClick={() => setTestTarget(null)} type="button">×</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
                Enviar prueba de <strong style={{ color: 'var(--text-primary)' }}>{formatEvento(testTarget.evento)}</strong>{' '}
                via <span className={`notif-card__canal-badge notif-card__canal-badge--${testTarget.canal}`} style={{ fontSize: '0.72rem', padding: '2px 8px' }}>
                  {testTarget.canal === 'whatsapp' ? 'WhatsApp' : 'Email'}
                </span>
              </p>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">
                  {testTarget.canal === 'whatsapp' ? 'Número de WhatsApp' : 'Email'}
                </label>
                <input
                  className="form-input"
                  value={testAddress}
                  onChange={e => setTestAddress(e.target.value)}
                  placeholder={testTarget.canal === 'whatsapp' ? '+51 999 999 999' : 'test@email.com'}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setTestTarget(null)} type="button">Cancelar</button>
              <button
                className="btn btn-primary"
                onClick={sendTest}
                disabled={testSending || !testAddress.trim()}
                type="button"
              >
                <IconSend size={14} /> {testSending ? 'Enviando...' : 'Enviar Prueba'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
